import {
  isCenterpiece,
  TILE_SHAPES,
  type TilePreviewSpec,
} from '@mixtiles/web-backend-shared'
import { type RemotePhoto } from 'components/PhotoBank/PhotoBank.types'
import { getShapeFromSize } from 'services/ShapeManager'
import { getTileStyleData } from 'services/tilesStyling'
import { type TileData } from 'stores/tilesStore/tiles/tileSchemas'
import { getCropTargetAspectRatio } from 'utils/cropRatioUtils'

type PhotoTile = TileData & { uid: string }

const isPreviewableTile =
  (remotePhotoByUid: Record<string, RemotePhoto>) =>
  (tile: TileData): tile is PhotoTile =>
    typeof tile.uid === 'string' &&
    !isCenterpiece(tile) &&
    !!remotePhotoByUid[tile.uid]

const isSquareTile = (tile: TileData): boolean =>
  getShapeFromSize(tile.tileSize) === TILE_SHAPES.SQUARE

function selectMainTile(tiles: PhotoTile[]): PhotoTile | undefined {
  return tiles.find(isSquareTile) ?? tiles[0]
}

function selectBackgroundTile(
  main: PhotoTile,
  others: PhotoTile[]
): PhotoTile | null {
  if (others.length === 0) return null
  const mainColor = getTileStyleData(main).frameColor
  const hasDifferentColor = (tile: TileData) =>
    getTileStyleData(tile).frameColor !== mainColor
  return (
    others.find((tile) => isSquareTile(tile) && hasDifferentColor(tile)) ??
    others.find(hasDifferentColor) ??
    others.find(isSquareTile) ??
    others[0]
  )
}

function getCurrentCrop(tile: TileData) {
  const aspectRatio = getCropTargetAspectRatio({
    tileSize: tile.tileSize,
    materialType: tile.materialType,
    mattingType: getTileStyleData(tile).matting,
  })
  return tile.cropParams[aspectRatio]
}

function tileToPreviewSpec(
  tile: PhotoTile,
  remotePhoto: RemotePhoto
): TilePreviewSpec {
  const crop = getCurrentCrop(tile)
  return {
    photoUrl: remotePhoto.originalUrl,
    crop: crop && {
      x: crop.x,
      y: crop.y,
      width: crop.width,
      height: crop.height,
    },
    materialType: tile.materialType,
    frameColor: getTileStyleData(tile).frameColor,
    tileSize: tile.tileSize,
  }
}

export function selectPreviewTileSpecs({
  tiles,
  remotePhotoByUid,
}: {
  tiles: TileData[]
  remotePhotoByUid: Record<string, RemotePhoto>
}): TilePreviewSpec[] {
  const photoTiles = tiles.filter(isPreviewableTile(remotePhotoByUid))
  const main = selectMainTile(photoTiles)
  if (!main) return []
  const background = selectBackgroundTile(
    main,
    photoTiles.filter((tile) => tile !== main)
  )
  const ordered = background ? [main, background] : [main]
  return ordered.map((tile) =>
    tileToPreviewSpec(tile, remotePhotoByUid[tile.uid])
  )
}
